// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/alert.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
"use client";

import {
  AlertDescription as AlertDescriptionV1,
  AlertTitle as AlertTitleV1,
  Alert as AlertV1,
} from "@repo/design-system/components/ui/alert";
import {
  AlertDialogContent as AlertDialogContentV1,
  AlertDialogDescription as AlertDialogDescriptionV1,
  AlertDialogFooter as AlertDialogFooterV1,
  AlertDialogHeader as AlertDialogHeaderV1,
  AlertDialogTitle as AlertDialogTitleV1,
} from "@repo/design-system/components/ui/alert-dialog";
import { cn } from "@repo/design-system/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  XIcon,
} from "lucide-react";
import { AlertDialog as AlertDialogPrimitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";
import { Button, type ButtonV2Props } from "./button";

/**
 * v2 Alert + AlertDialog — restyles v1's `Alert` (`components/ui/alert.tsx`)
 * and `AlertDialog` (`components/ui/alert-dialog.tsx`, Radix-backed) to match
 * the Figma "Remote Leverage — Design System" file, "↳ Alert" page `37:40`,
 * component set `37:445`. v1's `role="alert"` and every bit of Radix
 * AlertDialog behavior (focus trap, no dismiss-on-backdrop, Escape, portal,
 * the `systemAlert` z-tier switch) stay v1's own — this file only overrides
 * the visual layer.
 *
 * Exact from Figma:
 * - `variant="default"` / `"info"` ← Variant=Default (`37:446`): blue-50
 *   tinted banner, blue-200 border, 12px radius, 12px/16px padding (set as
 *   11px/15px: Figma's 1px stroke sits inside that padding, CSS's border
 *   adds to it — so the content lands on Figma's exact offsets), 12px
 *   gaps, 16px icon nudged 2px down, 14px/1.56 medium title (single line,
 *   ellipsized) over a 14px/1.56 regular description 4px below.
 * - `variant="destructive"` / `"error"` ← Variant=Destructive (`37:454`):
 *   same anatomy on a white surface with a neutral border, title and
 *   description both red.
 * - Close button ← the instance's ghost icon button (`149:4034`, 40×40,
 *   8px radius, 16px X) — rendered with v2 `Button variant="ghost"
 *   size="icon"`, which already matches it; only the X color is set here.
 *   It only renders when `onClose` is passed (Figma's `button` toggle).
 *
 * Interpolated (no Figma node — see alert.tokens.css for the values):
 * - `variant="success"` and `"warning"`: the info banner's anatomy with
 *   green/orange surfaces and the v2 Badge's green/orange text colors on
 *   the icon. Title/description stay neutral, like info.
 * - Icons: Figma's `iconName` is an instance-swap slot whose placeholder is
 *   "Icon / CircleCheck" in BOTH drawn variants (blue in Default, red in
 *   Destructive) — a placeholder, not a semantic choice. Each variant gets a
 *   lucide icon matched by intent instead (info → `Info`, success →
 *   `CircleCheck`, warning → `TriangleAlert`, error → `CircleAlert`); the
 *   Figma stroke colors are kept. Pass `icon` to swap it, or `icon={null}`
 *   to hide it (Figma's `icon` toggle).
 * - Close button hover: Figma draws no hover; it's v2 Button's ghost hover.
 * - AlertDialog: the Alert page has no dialog at all, so it takes the v2
 *   Modal's look (`v2/modal.tsx`, Figma Type=2 `197:1018`): 500px wide,
 *   20px radius, gray-100 surface, 20px/1.44 medium title, 14px/1.56
 *   secondary description, and a footer of equal-width `lg` buttons. Its
 *   colors alias modal.tokens.css. Modal's own parts can't be reused
 *   directly — they are Radix `Dialog` parts and would lose AlertDialog's
 *   context (title/description labelling) — so the same classes are applied
 *   to v1's AlertDialog parts instead. There is no corner X: an alert
 *   dialog is dismissed only through its actions, so the header keeps the
 *   full 24px right padding rather than Modal's 64px close-button gutter.
 * - `AlertDialogAction`/`AlertDialogCancel` render v2 `Button` (default
 *   purple / `outline`, size `lg`, like Modal's footer) through Radix's
 *   `asChild`, instead of v1's `buttonVariants()`. Pass
 *   `variant="destructive"` on the action for a destructive confirmation.
 *
 * Layout note: v1's Alert is a 2-column grid keyed off a direct `<svg>`
 * child. Figma's banner is a row (icon + text block, then the close button
 * centered on the right), so the root is switched to flex and the icon,
 * text and close button are laid out by `Alert` itself — pass the icon via
 * the `icon` prop, not as a raw `<svg>` child.
 */

const alertVariants = cva(
  cn(
    "ds-v2 flex items-center gap-3 rounded-(--v2-alert-radius) px-[15px] py-[11px] text-sm leading-[1.56]",
    "text-(--v2-alert-title) [&_[data-slot=alert-description]]:text-(--v2-alert-description)"
  ),
  {
    variants: {
      variant: {
        default:
          "border-(--v2-alert-info-border) bg-(--v2-alert-info-bg) [&_[data-slot=alert-icon]]:text-(--v2-alert-info-icon)",
        info: "border-(--v2-alert-info-border) bg-(--v2-alert-info-bg) [&_[data-slot=alert-icon]]:text-(--v2-alert-info-icon)",
        success:
          "border-(--v2-alert-success-border) bg-(--v2-alert-success-bg) [&_[data-slot=alert-icon]]:text-(--v2-alert-success-icon)",
        warning:
          "border-(--v2-alert-warning-border) bg-(--v2-alert-warning-bg) [&_[data-slot=alert-icon]]:text-(--v2-alert-warning-icon)",
        destructive:
          "border-(--v2-alert-error-border) bg-(--v2-alert-error-bg) text-(--v2-alert-error-text) [&_[data-slot=alert-description]]:text-(--v2-alert-error-text) [&_[data-slot=alert-icon]]:text-(--v2-alert-error-icon)",
        error:
          "border-(--v2-alert-error-border) bg-(--v2-alert-error-bg) text-(--v2-alert-error-text) [&_[data-slot=alert-description]]:text-(--v2-alert-error-text) [&_[data-slot=alert-icon]]:text-(--v2-alert-error-icon)",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>;

const DEFAULT_ICONS: Record<AlertVariant, ReactNode> = {
  default: <Info />,
  info: <Info />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  destructive: <CircleAlert />,
  error: <CircleAlert />,
};

type AlertProps = Omit<ComponentProps<typeof AlertV1>, "variant"> & {
  variant?: AlertVariant;
  /** Replaces the variant's default icon; `null` hides the icon. */
  icon?: ReactNode;
  /** Renders the trailing close button when set. */
  onClose?: () => void;
  /** Accessible label for the close button. */
  closeLabel?: string;
};

function Alert({
  className,
  variant = "default",
  icon,
  onClose,
  closeLabel = "Dismiss",
  children,
  ...props
}: AlertProps) {
  const resolvedIcon = icon === undefined ? DEFAULT_ICONS[variant] : icon;
  return (
    <AlertV1
      className={cn(alertVariants({ variant }), className)}
      data-variant={variant}
      {...props}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        {resolvedIcon ? (
          <span
            aria-hidden="true"
            className="flex shrink-0 pt-0.5 [&>svg]:size-4"
            data-slot="alert-icon"
          >
            {resolvedIcon}
          </span>
        ) : null}
        <div
          className="flex min-w-0 flex-1 flex-col justify-center gap-1"
          data-slot="alert-content"
        >
          {children}
        </div>
      </div>
      {onClose ? (
        <Button
          aria-label={closeLabel}
          className="text-(--v2-alert-close-fg)"
          data-slot="alert-close"
          onClick={onClose}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon />
        </Button>
      ) : null}
    </AlertV1>
  );
}

function AlertTitle({
  className,
  ...props
}: ComponentProps<typeof AlertTitleV1>) {
  return (
    <AlertTitleV1
      className={cn(
        "col-start-auto min-h-0 font-medium leading-[1.56] tracking-normal",
        className
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: ComponentProps<typeof AlertDescriptionV1>) {
  return (
    <AlertDescriptionV1
      className={cn(
        "col-start-auto text-sm leading-[1.56] [&_p]:leading-[1.56]",
        className
      )}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle };
export type { AlertProps, AlertVariant };

/* ─── AlertDialog (wraps alert-dialog.tsx, styled like v2 Modal) ────────── */

function AlertDialogContent({
  className,
  ...props
}: ComponentProps<typeof AlertDialogContentV1>) {
  return (
    <AlertDialogContentV1
      className={cn(
        "ds-v2 flex flex-col gap-0 rounded-(--v2-alert-dialog-radius) border-0 bg-(--v2-alert-dialog-bg) p-0 sm:max-w-[500px]",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogHeader({
  className,
  ...props
}: ComponentProps<typeof AlertDialogHeaderV1>) {
  return (
    <AlertDialogHeaderV1
      className={cn("gap-1 p-6 text-left sm:text-left", className)}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  ...props
}: ComponentProps<typeof AlertDialogFooterV1>) {
  return (
    <AlertDialogFooterV1
      className={cn(
        "flex-row items-stretch gap-3 px-6 pt-2 pb-6 sm:flex-row sm:justify-stretch [&>*]:flex-1",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: ComponentProps<typeof AlertDialogTitleV1>) {
  return (
    <AlertDialogTitleV1
      className={cn(
        "font-medium text-(--v2-alert-dialog-title) text-xl leading-[1.44]",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: ComponentProps<typeof AlertDialogDescriptionV1>) {
  return (
    <AlertDialogDescriptionV1
      className={cn(
        "text-(--v2-alert-dialog-description) text-sm leading-[1.56]",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogAction({
  variant = "default",
  size = "lg",
  ...props
}: ButtonV2Props) {
  return (
    <AlertDialogPrimitive.Action asChild>
      <Button
        data-slot="alert-dialog-action"
        size={size}
        variant={variant}
        {...props}
      />
    </AlertDialogPrimitive.Action>
  );
}

function AlertDialogCancel({
  variant = "outline",
  size = "lg",
  ...props
}: ButtonV2Props) {
  return (
    <AlertDialogPrimitive.Cancel asChild>
      <Button
        data-slot="alert-dialog-cancel"
        size={size}
        variant={variant}
        {...props}
      />
    </AlertDialogPrimitive.Cancel>
  );
}

// biome-ignore lint/performance/noBarrelFile: re-exporting v1's AlertDialog behavior-only parts (root/trigger/portal/overlay) that need no visual changes verbatim
export {
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTrigger,
} from "@repo/design-system/components/ui/alert-dialog";

export {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
};
